import { AppThunk } from '../types';
import apis from '../../utils/apis';
import { showFeedbackModal } from './UserFeedbackActions';
import { handleApiError } from './utils/utils';

// =====================================================================
// TYPES
// =====================================================================

export interface RecycleBinItem {
  id: string;
  uuid: string;
  entity_type: string;
  name: string;
  deleted_at: string;
  deleted_by_user_uuid?: string;
  days_in_trash: number;
  days_remaining: number;
  // Creator / deleter metadata (resolved by backend)
  created_by_name?: string;
  created_by_role?: string;
  deleted_by_name?: string;
  deleted_by_role?: string;
  // File/folder specific fields
  FolderID?: string;
  FileID?: string;
  OriginalLocation?: string;
  Size?: number;
  MimeType?: string;
  Tags?: Array<{ id: number; uuid: string; name: string; color: string }>;
}

export interface RecycleBinParams {
  page?: number;
  per_page?: number;
  entity_type?: string;
  search?: string;
  sort_by?: string;
  sort_order?: string;
  user_only?: boolean;
}

export interface RecycleBinStats {
  [entityType: string]: number;
}

export interface RecycleBinResponse {
  success: boolean;
  items: RecycleBinItem[];
  pagination: {
    page: number;
    per_page: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  stats: RecycleBinStats;
  retention_days: number;
}

// =====================================================================
// FETCH RECYCLE BIN ITEMS
// =====================================================================

export const fetchRecycleBin = (
  params?: RecycleBinParams
): AppThunk<Promise<RecycleBinResponse | null>> => async (dispatch) => {
  try {
    const response = await apis.get('/recycle-bin', { params });
    if (response?.data?.success) {
      return response.data as RecycleBinResponse;
    }
    return null;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return null;
  }
};

// =====================================================================
// RESTORE ITEMS
// =====================================================================

export const restoreRecycleBinItems = (
  items: Array<{ entity_type: string; id: string }>
): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.post('/recycle-bin/restore', { items });

    if (response?.data?.success || response?.status === 207) {
      const restored = response.data?.results?.filter((r: any) => r.success).length || 0;
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${restored} item${restored !== 1 ? 's' : ''} restored successfully`,
        modalType: 'success',
        duration: 3000,
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

// =====================================================================
// PERMANENT DELETE ITEMS
// =====================================================================

export const permanentDeleteRecycleBinItems = (
  items: Array<{ entity_type: string; id: string }>
): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.post('/recycle-bin/permanent-delete', { items });

    if (response?.data?.success || response?.status === 207) {
      const deleted = response.data?.results?.filter((r: any) => r.success).length || 0;
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${deleted} item${deleted !== 1 ? 's' : ''} permanently deleted`,
        modalType: 'success',
        duration: 3000,
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

// =====================================================================
// EMPTY RECYCLE BIN
// =====================================================================

export const emptyRecycleBin = (): AppThunk<Promise<boolean>> => async (dispatch) => {
  try {
    const response = await apis.post('/recycle-bin/empty');

    if (response?.data?.success) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: 'Recycle bin emptied successfully',
        modalType: 'success',
        duration: 3000,
      }));
      return true;
    }

    handleApiError(response, dispatch);
    return false;
  } catch (error: any) {
    handleApiError(error, dispatch);
    return false;
  }
};

// =====================================================================
// FETCH STATS
// =====================================================================

export const fetchRecycleBinStats = (): AppThunk<Promise<{
  stats: RecycleBinStats;
  total_items: number;
  retention_days: number;
} | null>> => async (dispatch) => {
  try {
    const response = await apis.get('/recycle-bin/stats');
    if (response?.data?.success) {
      return {
        stats: response.data.stats,
        total_items: response.data.total_items,
        retention_days: response.data.retention_days,
      };
    }
    return null;
  } catch (error: any) {
    return null;
  }
};
